import React, { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Mail, Lock, User, Eye, EyeOff, Check, X } from "lucide-react";
import { useI18n, useLocalizedPath } from "../i18n/I18nContext";
import { useAuth } from "../contexts/AuthContext";
import { TID } from "../constants/testIds";
import { toast } from "sonner";

function strengthChecks(pw) {
  return {
    length: pw.length >= 8,
    upper: /[A-Z]/.test(pw),
    lower: /[a-z]/.test(pw),
    number: /\d/.test(pw),
  };
}

const LABELS = {
  en: {
    rules: {
      length: "8 characters or more",
      upper: "One uppercase letter",
      lower: "One lowercase letter",
      number: "One number",
    },
    confirmLabel: "Confirm password",
    confirmMismatch: "Passwords don't match.",
    terms: "I agree to the terms and privacy policy.",
    termsRequired: "Please accept the terms to continue.",
  },
  ro: {
    rules: {
      length: "8 caractere sau mai multe",
      upper: "O literă mare",
      lower: "O literă mică",
      number: "O cifră",
    },
    confirmLabel: "Confirmă parola",
    confirmMismatch: "Parolele nu se potrivesc.",
    terms: "Sunt de acord cu termenii și politica de confidențialitate.",
    termsRequired: "Te rugăm să accepți termenii pentru a continua.",
  },
};

export default function Register() {
  const { t, lang } = useI18n();
  const lp = useLocalizedPath();
  const { register } = useAuth();
  const navigate = useNavigate();
  const L = LABELS[lang];

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [showCpw, setShowCpw] = useState(false);
  const [terms, setTerms] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const rules = useMemo(() => strengthChecks(password), [password]);
  const allRulesOk = Object.values(rules).every(Boolean);
  const confirmOk = confirm.length > 0 && confirm === password;
  const canSubmit = name.trim().length > 0 && email.length > 3 && allRulesOk && confirmOk && terms && !loading;

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!terms) {
      setError(L.termsRequired);
      return;
    }
    if (!confirmOk) {
      setError(L.confirmMismatch);
      return;
    }
    if (!allRulesOk) return;

    setLoading(true);
    const res = await register({
      name: name.trim(),
      email: email.trim(),
      password,
      language: lang,
      terms_accepted: true,
    });
    setLoading(false);
    if (res.ok) {
      localStorage.setItem("lp_pending_email", res.email);
      toast.success(lang === "en" ? "Account created. Check your inbox." : "Cont creat. Verifică-ți inbox-ul.");
      navigate(lp("/check-inbox"), { state: { email: res.email } });
    } else {
      setError(res.error);
    }
  };

  return (
    <div className="container py-16 md:py-24 max-w-md">
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.55, ease: [0.16, 1, 0.3, 1] }}
        className="glass rounded-3xl p-8 md:p-10"
      >
        <h1 className="font-display text-card-lg md:text-section tracking-tight leading-[1.05]">{t.auth.registerTitle}</h1>
        <p className="mt-3 text-cream-muted">{t.auth.registerSub}</p>

        <form onSubmit={onSubmit} className="mt-10 space-y-5">
          <Field icon={<User className="h-4 w-4 text-cream-dim" />} label={t.auth.name}>
            <input
              data-testid={TID.authName}
              required
              autoComplete="name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={t.auth.name}
              className="input-bare"
            />
          </Field>

          <Field icon={<Mail className="h-4 w-4 text-cream-dim" />} label={t.auth.email}>
            <input
              data-testid={TID.authEmail}
              type="email"
              required
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value.trim())}
              placeholder={t.auth.email}
              className="input-bare"
            />
          </Field>

          <Field icon={<Lock className="h-4 w-4 text-cream-dim" />} label={t.auth.password}>
            <input
              data-testid={TID.authPassword}
              type={showPw ? "text" : "password"}
              required
              minLength={8}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder={t.auth.password}
              autoComplete="new-password"
              className="input-bare"
            />
            <button
              type="button"
              onClick={() => setShowPw((s) => !s)}
              aria-label="toggle password"
              data-testid="toggle-password"
              className="text-cream-dim hover:text-cream"
            >
              {showPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </button>
          </Field>

          {/* Password rules */}
          <ul className="grid grid-cols-2 gap-y-2 gap-x-3 -mt-1">
            <Rule ok={rules.length} label={L.rules.length} />
            <Rule ok={rules.number} label={L.rules.number} />
            <Rule ok={rules.upper} label={L.rules.upper} />
            <Rule ok={rules.lower} label={L.rules.lower} />
          </ul>

          <Field icon={<Lock className="h-4 w-4 text-cream-dim" />} label={L.confirmLabel}>
            <input
              data-testid="auth-confirm"
              type={showCpw ? "text" : "password"}
              required
              minLength={8}
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              placeholder={L.confirmLabel}
              autoComplete="new-password"
              className="input-bare"
            />
            <button
              type="button"
              onClick={() => setShowCpw((s) => !s)}
              aria-label="toggle confirm password"
              className="text-cream-dim hover:text-cream"
            >
              {showCpw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </button>
          </Field>
          {confirm.length > 0 && !confirmOk && (
            <p className="text-caption text-red-300/90 -mt-2">{L.confirmMismatch}</p>
          )}

          <label className="flex items-start gap-3 cursor-pointer select-none">
            <input
              data-testid="auth-terms"
              type="checkbox"
              checked={terms}
              onChange={(e) => setTerms(e.target.checked)}
              className="mt-1 h-4 w-4 rounded border-white/15 bg-obsidian-700 accent-[#D4AF37]"
            />
            <span className="text-caption text-cream-muted leading-relaxed">
              {L.terms.split(" ").slice(0, -3).join(" ")}{" "}
              <Link to={lp("/legal/terms")} className="text-cream hover:text-gold underline-offset-4 hover:underline">
                {t.footer.terms.toLowerCase()}
              </Link>
              {" & "}
              <Link to={lp("/legal/privacy")} className="text-cream hover:text-gold underline-offset-4 hover:underline">
                {t.footer.privacy.toLowerCase()}
              </Link>
              .
            </span>
          </label>

          {error && (
            <p data-testid={TID.authError} className="text-caption text-red-300/90">
              {error}
            </p>
          )}

          <button
            data-testid={TID.authSubmit}
            type="submit"
            disabled={!canSubmit}
            className="btn btn-gold btn-lg w-full disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {loading ? t.common.sending : t.auth.submitRegister}
            <ArrowRight className="h-4 w-4" />
          </button>
        </form>

        <Link to={lp("/login")} className="block mt-8 text-center text-caption text-cream-muted hover:text-cream">
          {t.auth.switchToLogin}
        </Link>
      </motion.div>
    </div>
  );
}

export function Field({ icon, label, children }) {
  return (
    <div>
      {label && (
        <label className="block font-mono-label text-cream-dim mb-2">{label}</label>
      )}
      <div className="flex items-center gap-3 input-field !py-3 focus-within:!border-gold/55">
        {icon}
        {children}
      </div>
    </div>
  );
}

function Rule({ ok, label }) {
  return (
    <li className={`flex items-center gap-2 text-caption ${ok ? "text-gold" : "text-cream-dim"}`}>
      {ok ? <Check className="h-3.5 w-3.5" /> : <X className="h-3.5 w-3.5 opacity-50" />}
      <span>{label}</span>
    </li>
  );
}
